import { NgStyle } from '@angular/common'
import { Component, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCamera, faVideo, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'

import { EquipmentRequest, ListItem, RepositoryModal, TimeUnit, TimeUnitEx } from '../../shared'
import { AvailabilityComponent } from './availability.component'

@Component({
    templateUrl: './request.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle],
})
export class RequestComponent extends RepositoryModal<EquipmentRequest> implements OnInit {
    faCamera = faCamera
    faVideo = faVideo
    faX = faX

    jobMap: ListItem<string>[] = []
    jobMap2: ListItem<string>[] = []
    equipmentMap: ListItem<string>[] = []
    userMap: ListItem<string>[] = []
    categoryMap: ListItem<string>[] = []
    rentalTypeMap: ListItem<number>[] = [new ListItem(0, 'Not a Rental'), new ListItem(1, 'New Rental'), new ListItem(2, 'Rental Return')]
    vendorMap: ListItem<string>[] = []
    billingCycleMap: ListItem<number>[] = [
        new ListItem(TimeUnit.Day, TimeUnitEx.toDisplayAdverb(TimeUnit.Day)),
        new ListItem(TimeUnit.Week, TimeUnitEx.toDisplayWithUnit(TimeUnit.Week)),
        new ListItem(TimeUnit.TwoWeek, TimeUnitEx.toDisplayWithUnit(TimeUnit.TwoWeek)),
        new ListItem(TimeUnit.Month, TimeUnitEx.toDisplayWithUnit(TimeUnit.Month)),
        new ListItem(TimeUnit.OverMonth, TimeUnitEx.toDisplayWithUnit(TimeUnit.OverMonth)),
    ]
    requestFromEquipment = false

    @ViewChild('videoInput') videoInputRef!: HTMLInputElement
    images: any[] = []
    videos: any[] = []
    progress = 0
    previewSrc = ''

    ngOnInit() {
        // Since this is the modal, the repo should already be loaded from the Requests Component.
        const availabilityComponent = this.editOptions.initializeParams as AvailabilityComponent
        this.vendorMap = this.services.data.vendors.items.map(x => new ListItem(x.name, x.name))
        this.userMap = availabilityComponent.activeUserMap.collectionView.items.map(x => new ListItem(x.id, x.name))
    }

    protected onInitialized() {
        const availabilityComponent = this.editOptions.initializeParams as AvailabilityComponent

        this.target.requestedById = availabilityComponent.activeUserMap.collectionView.items.filter(
            x => x.id == this.services.authentication.hrref,
        )[0].id

        const jobMap1 = availabilityComponent.allJobs
            .orderByDescending(x => x.id)
            .map(x => new ListItem(x.id + ' ' + x.name, x.id + ' ' + x.name))
        jobMap1.unshift(new ListItem('VanCon HQ / Shop', 'VanCon HQ / Shop'))
        this.jobMap = jobMap1

        const jobMap2 = availabilityComponent.allJobs
            .orderByDescending(x => x.id)
            .map(x => new ListItem(x.id + ' ' + x.name, x.id + ' ' + x.name))
        jobMap2.unshift(new ListItem('VanCon HQ / Shop', 'VanCon HQ / Shop'))
        this.jobMap2 = jobMap2

        this.equipmentMap = availabilityComponent.grid.items.map(x => new ListItem(x.equipment, x.description))
        this.categoryMap = availabilityComponent.categories.map(x => new ListItem(x, x))

        if (availabilityComponent.isAddingRequestFromEquipment) {
            this.requestFromEquipment = true
            let item = availabilityComponent.grid.selectedItem
            if (!item) {
                item = this.equipmentMap[0].value
            }
            this.target.rentalType = 0
            this.target.equipment = item.equipment
            if (item.jobGpsId && item.jobGpsId === 'VanCon HQ / Shop') {
                this.target.fromJob = item.jobGpsId
            } else if (item.jobGpsId) {
                const match = availabilityComponent.allJobs.find(x => x.id === item.jobGpsId)
                if (match) {
                    this.target.fromJob = match.id + ' ' + match.name
                }
            }
        }
    }

    protected onBeforeSave() {
        if (this.target.rentalType === 1) {
            if (!this.target.fromJob) {
                this.target.fromJob = this.vendorMap[0].value
            }
            if (!this.target.category) {
                this.services.notification.warning('Please choose a category for the rental equipment')
                return false
            }
        }
        if (this.target.rentalType === 2) {
            if (!this.target.forJob) {
                this.target.forJob = this.vendorMap[0].value
            }
        }
        return true
    }

    async onFileChanged(event) {
        // When the user picks images, we will compress and store them locally.
        // They will get uploaded after the item is saved.
        const files = event.target.files
        const progress = (p: number) => {
            this.progress = p
        }
        for (const file of files) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }
    }

    async onFileChangedVideo(event) {
        // When the user picks videos, we will store them locally.
        // They will get uploaded after the item is saved.
        const files = event.target.files
        for (const video of files) {
            const reader = new FileReader()

            reader.onload = e => {
                const dataURL = e.target?.result as string
                this.previewSrc = dataURL
                video.previewSrc = dataURL
            }

            reader.readAsDataURL(video)

            this.videos.push(video)
        }
        this.videoInputRef.value = ''
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: result => {
                if (this.images.length === 0 && this.videos.length === 0) {
                    resolveSave(result)
                    return
                }

                // If the saving of the record is successful, we will upload any images and videos
                const uploadData = new FormData()
                uploadData.append('id', result.id)
                for (let i = 0; i < this.images.length; i++) {
                    uploadData.append('myFile' + i, this.images[i], this.images[i].name)
                }
                for (let i = 0; i < this.videos.length; i++) {
                    uploadData.append('myFile' + i, this.videos[i], this.videos[i].name)
                }
                this.services.data.post('api/EquipmentRequest/UploadImages', uploadData).subscribe({
                    next: uploadResult => {
                        resolveSave(uploadResult)
                    },
                    error: errorResult => {
                        rejectSave(errorResult)
                    },
                })
            },
            error: errorResult => {
                rejectSave(errorResult)
            },
        })
    }

    removeImage(image) {
        this.images.remove(image)
    }

    removeVideo(video) {
        this.videos.remove(video)
    }

    canShowImage(url) {
        return url.indexOf('mp4') === -1
    }

    canShowVideo(url) {
        return url.indexOf('mp4') > -1
    }
}
